/* ───────────────────────────────────────────
   Navbar – navegación principal LS
   ─────────────────────────────────────────── */
import React, { useEffect, useState, useContext, useRef } from "react";
import { Link } from "react-router-dom";
import { FiHome, FiTool, FiBookOpen, FiUser, FiBarChart2 } from "react-icons/fi";
import { FaBuilding } from "react-icons/fa";
import {
  AiOutlineCaretDown,
  AiOutlineMenu,
  AiOutlineClose,
} from "react-icons/ai";
import { useTranslation } from "react-i18next";
import KeycloakContext from "../../keycloakContext";
import logo from "../assets/LV.png";
import { logoutToLivingSoilsHome } from "../utils/keycloakNavigation";
import { dispatchProfessorTutorialRestart } from "../utils/professorTutorialEvents";
import { SchoolIcon, Binary } from "lucide-react";
import useSelectedVignetteAsset from "../hooks/useSelectedVignetteAsset";

const Navbar = () => {
  const keycloak = useContext(KeycloakContext);
  const { t, i18n } = useTranslation();

  // ─────────── estados ───────────
  const [active, setActive] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navRef = useRef(null);
  const [avatarUrl, setAvatarUrl] = useState("");
  const { selectedVignettePath } = useSelectedVignetteAsset();

  // dropdowns móviles
  const [mostreigOpen, setMostreigOpen] = useState(false);
  const [recursosOpen, setRecursosOpen] = useState(false);

  // ─────────── efectos ───────────
  useEffect(() => {
    setAuthenticated(keycloak.authenticated);

    if (keycloak.authenticated) {
      const groups = keycloak.tokenParsed?.groups || [];
      setUserRole(
        groups.includes("/professor")
          ? "professor"
          : groups.includes("/admin")
          ? "admin"
          : "alumne"
      );

      const pic = keycloak.tokenParsed?.picture;
      if (pic) {
        setAvatarUrl(pic);
        localStorage.setItem("avatarUrl", pic);
      } else {
        const saved = localStorage.getItem("avatarUrl");
        if (saved) setAvatarUrl(saved);
      }
    }
  }, [keycloak]);

  // sincroniza avatarUrl entre pestañas
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "avatarUrl") {
        setAvatarUrl(e.newValue || "/images/default-avatar.png");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // ─────────── helpers ───────────
  const handleLogin = () => keycloak.login();
  const handleLogout = () => {
    localStorage.removeItem("avatarUrl");
    logoutToLivingSoilsHome(keycloak);
  };
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLanguageOpen(false);
  };


  useEffect(() => {
    const root = document.documentElement;
    const navEl = navRef.current;
    if (!navEl) return undefined;

    const updateNavbarHeight = () => {
      root.style.setProperty("--app-navbar-height", `${navEl.offsetHeight || 0}px`);
    };

    updateNavbarHeight();

    if (typeof ResizeObserver === "function") {
      const observer = new ResizeObserver(updateNavbarHeight);
      observer.observe(navEl);
      return () => {
        observer.disconnect();
        root.style.removeProperty("--app-navbar-height");
      };
    }

    window.addEventListener("resize", updateNavbarHeight);
    return () => {
      window.removeEventListener("resize", updateNavbarHeight);
      root.style.removeProperty("--app-navbar-height");
    };
  }, []);

  if (!i18n.isInitialized) return null;
  const languageCode = (i18n.language || "ca").toUpperCase();
  const accountAvatarUrl = selectedVignettePath || avatarUrl || localStorage.getItem("avatarUrl") || "/images/default-avatar.png";

  // ─────────── JSX ───────────
  return (
    <nav ref={navRef} data-app-navbar className="navbar w-full fixed top-0 z-20 bg-white shadow-lg">
      <div className="w-full max-w-[1600px] mx-auto px-4 xl:px-10 py-4 flex items-center justify-between">
        {/* logo */}
        <Link to="/" onClick={() => setActive("Inici")}>
          <img
            src={logo}
            alt="Living Soils Logo"
            className="w-28 xl:w-36 object-contain"
          />
        </Link>

        {/* menú escritorio */}
        <div className="flex-1 hidden xl:flex justify-center">
          <ul className="flex items-center gap-6 text-black">
            {/* Inici */}
            <li className={active === "Inici" ? "text-blue-600" : ""}>
              <Link
                to="/"
                className="flex items-center gap-1"
                onClick={() => setActive("Inici")}
              >
                <FiHome /> {t("Inici")}
              </Link>
            </li>

            {/* Projecte */}
            <li className={active === "Projecte" ? "text-blue-600" : ""}>
              <Link
                to="/projecte"
                className="flex items-center gap-1"
                onClick={() => setActive("Projecte")}
              >
                <FiTool /> {t("Projecte")}
              </Link>
            </li>

            {/* STEAMcat */}
            <li className={active === "STEAMcat" ? "text-blue-600" : ""}>
              <Link
                to="/steamcat"
                className="flex items-center gap-1"
                onClick={() => setActive("STEAMcat")}
              >
                <Binary /> {t("steamcat.menu")}
              </Link>
            </li>

            {/* Mostreig */}
            <li className="relative group">
              <button className="flex items-center">
                <FiBookOpen className="mr-1" /> {t("Mostreig")}
                <AiOutlineCaretDown className="ml-1" />
              </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2
                hidden group-hover:flex flex-col items-center
                bg-white shadow-xl rounded-xl px-4 py-4
                space-y-3 z-10">
                <Link to="/instructions" className="block submenu-link">
                  {t("Instruccions")}
                </Link>
                {authenticated && (
                  <Link to="/applicatiu" className="block submenu-link">
                    {t("Aplicatiu")}
                  </Link>
                )}
              </div>
            </li>
{/* Dashboard */}
<li className={active === "Dashboard" ? "text-blue-600" : ""}>
  <Link
    to="/dashboard-admin"
    className="flex items-center gap-1"
    onClick={() => setActive("Dashboard")}
  >
    <FiBarChart2 /> Dashboard
  </Link>
</li>
            {/* Recursos */}
            {authenticated && (
              <li className="relative group">
                <button className="flex items-center">
                  {t("Recursos")}
                  <AiOutlineCaretDown className="ml-1" />
                </button>
<div className="absolute top-full left-1/2 -translate-x-1/2
                hidden group-hover:flex flex-col items-center
                bg-white shadow-xl rounded-xl px-4 py-4
                space-y-3 z-10">                  <Link to="/situacio" className="block submenu-link">
                    {t("Situacio daprenentatge")}
                  </Link>
                  {(userRole === "professor" || userRole === "admin") && (
                    <>
                      <Link to="/dossier" className="block submenu-link">
                        {t("ui.dossier.title", "Materials Didàctics")}
                      </Link>
                      <Link to="/materials" className="block submenu-link">
                        {t("Materials necessaris")}
                      </Link>
                    </>
                  )}
                </div>
              </li>
            )}

            {/* Aula */}
            {(userRole === "professor" || userRole === "admin") && (
              <li className={active === "Aula" ? "text-blue-600" : ""}>
                <Link
                  to="/aula"
                  data-professor-tutorial="classrooms-nav"
                  className="flex items-center"
                  onClick={() => setActive("Aula")}
                >
                  <SchoolIcon className="mr-1" /> {t("Aula")}
                </Link>
              </li>
            )}

            {/* Centres */}
            <li className="relative group">
              <button className="flex items-center">
                <FaBuilding className="mr-1" /> {t("Centres")}
                <AiOutlineCaretDown className="ml-1" />
              </button>
<div className="absolute top-full left-1/2 -translate-x-1/2
                hidden group-hover:flex flex-col items-center
                bg-white shadow-xl rounded-xl px-4 py-4
                space-y-3 z-10">               
                 <Link to="/map" className="block submenu-link">
                  {t("Mapa")}
                </Link>
              </div>
            </li>
          </ul>

        </div>

        {/* Right side */}
        <div className="navbarright flex items-center gap-4 xl:gap-6">
          {/* Selector de idioma */}
          <div className="relative">
            <button
              className="text-black px-2 py-1 xl:px-4 xl:py-2 bg-gray-100 rounded focus:outline-none"
              onClick={() => setLanguageOpen(!languageOpen)}
            >
              {languageCode} <AiOutlineCaretDown className="inline-block ml-1" />
            </button>
            {languageOpen && (
              <div className="absolute right-0 mt-2 w-24 xl:w-32 bg-white shadow-lg rounded-md z-10">
                <ul className="flex flex-col text-center text-black">
                  <li
                    className="px-4 py-2 cursor-pointer hover:bg-green-100"
                    onClick={() => changeLanguage("ca")}
                  >
                    CA
                  </li>
                  <li
                    className="px-4 py-2 cursor-pointer hover:bg-green-100"
                    onClick={() => changeLanguage("es")}
                  >
                    ES
                  </li>
                  <li
                    className="px-4 py-2 cursor-pointer hover:bg-green-100"
                    onClick={() => changeLanguage("en")}
                  >
                    EN
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Perfil de usuario – SOLO DESKTOP (xl) */}
          {authenticated ? (
            <div className="relative hidden xl:flex items-center group">
              <button className="flex items-center focus:outline-none">
                <div className="flex flex-col items-center">
                  <img
                    src={accountAvatarUrl}
                    alt={t("avatar.alt", "User avatar")}
                    className="w-8 h-8 rounded-full object-contain bg-green-50"
                    onError={() => setAvatarUrl("/images/default-avatar.png")}
                  />
                  <div className="text-xs text-gray-500">{userRole}</div>
                </div>
                <div className="ml-2 text-left">
                  <div className="text-xs text-black">
                    {(keycloak.tokenParsed?.name?.split(" ")[0]) ||
                      keycloak.tokenParsed?.preferred_username}
                  </div>
                </div>
                <AiOutlineCaretDown className="inline-block ml-1" />
              </button>



             {/* Dropdown – opciones de usuario */}
<div
  className="absolute top-full right-0
            hidden group-hover:flex flex-col gap-3
            bg-white shadow-xl rounded-xl
            py-4 px-4 z-20 min-w-[260px]"
  style={{ top: "100%" }}
>
  {/* ――― enlace Cuenta ――― */}
  <Link
    to="/account"
    className="w-full text-center text-[15px] font-medium text-gray-800
               px-5 py-3 rounded-lg shadow ring-1 ring-black/5
               hover:bg-green-50 hover:shadow-md transition"
  >
    {t("Compte")}
  </Link>

  {userRole === "professor" ? (
    <button
      type="button"
      onClick={dispatchProfessorTutorialRestart}
      className="w-full text-center text-[15px] font-medium text-emerald-700
                 px-5 py-3 rounded-lg shadow ring-1 ring-black/5
                 hover:bg-emerald-50 hover:shadow-md transition"
    >
      {t("professorOnboardingTutorial.actions.restart", "Tornar a obrir el tutorial")}
    </button>
  ) : null}

  {/* ――― botón Salir ――― */}
  <button
    onClick={handleLogout}
    className="w-full text-center text-[15px] font-medium text-red-600
               px-5 py-3 rounded-lg shadow ring-1 ring-black/5
               hover:bg-red-50 hover:shadow-md transition"
  >
    {t("Sortir")}
  </button>
</div>



            </div>
          ) : (
           <div className="hidden xl:flex items-center gap-3">
    {/* 👉 Registro profesorado */}
    <Link
      to="/registre-professorat"
      className="px-3 py-2 border border-green-500 text-green-600
                 rounded-lg hover:bg-green-50 transition text-sm font-medium
                 inline-flex items-center justify-center text-center"
    >
      Ets docent? Registra’t
    </Link>

    {/* Login */}
    <button
      className="px-4 py-2 bg-green-400 text-white rounded-lg
                 hover:bg-green-500 transition
                 inline-flex items-center justify-center text-center"
      onClick={handleLogin}
    >
      {t("Iniciar Sessió")}
    </button>
  </div>
)
          }
            {/* Contacte tècnic normal */}
            
{/* Contacte tècnic normal */}
<div className="relative group hidden xl:flex items-center justify-center">
  <Link
    to="/technical-support"
    onClick={() => {
      setActive("Suport tècnic");
      setMenuOpen(false);
    }}
    className="text-gray-600 hover:text-green-600 transition"
  >
    <FiTool className="text-2xl" />
  </Link>

  {/* Tooltip personalizado (hacia abajo) */}
  <span
    className="absolute top-full mt-2 px-2 py-1 text-sm text-white bg-gray-800 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50"
  >
    Suport tècnic
  </span>
</div>

            
            

          {/* Botón de menú para móvil */}
          <div className="xl:hidden">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="text-2xl text-black focus:outline-none"
            >
              {menuOpen ? <AiOutlineClose /> : <AiOutlineMenu />}
            </button>
          </div>
        </div>
      </div>

      {/* Menú móvil */}
      {menuOpen && (
        <div className="xl:hidden">
          <ul className="flex flex-col items-center gap-2 py-4 bg-white shadow-lg">
            {/* Inici */}
            <li className="relative flex items-center w-full">
              <Link
                to="/"
                className={`flex items-center gap-1 w-full px-4 py-2 ${
                  active === "Inici" ? "text-blue-600" : "text-black"
                }`}
                onClick={() => {
                  setActive("Inici");
                  setMenuOpen(false);
                }}
              >
                <FiHome className="inline-block" />
                {t("Inici")}
              </Link>
            </li>

            {/* Projecte */}
            <li className="relative flex items-center w-full">
              <Link
                to="/projecte"
                className={`flex items-center gap-1 w-full px-4 py-2 ${
                  active === "Projecte" ? "text-blue-600" : "text-black"
                }`}
                onClick={() => {
                  setActive("Projecte");
                  setMenuOpen(false);
                }}
              >
                <FiTool className="inline-block" />
                {t("Projecte")}
              </Link>
            </li>

            {/* STEAMcat (nuevo) */}
            <li className="relative flex items-center w-full">
              <Link
                to="/steamcat"
                className={`flex items-center gap-1 w-full px-4 py-2 ${
                  active === "STEAMcat" ? "text-blue-600" : "text-black"
                }`}
                onClick={() => {
                  setActive("STEAMcat");
                  setMenuOpen(false);
                }}
              >
                <Binary className="inline-block" />
                <span className="normal-case">{t("steamcat.menu")}</span>
              </Link>
            </li>

            {/* Mostreig */}
            <li className="relative flex items-center w-full">
              <button
                className="flex items-center text-black w-full px-4 py-2 focus:outline-none"
                onClick={() => setMostreigOpen(!mostreigOpen)}
              >
                <FiBookOpen className="inline-block mr-1" />
                {t("Mostreig")}
                <AiOutlineCaretDown className="ml-auto" />
              </button>
              {mostreigOpen && (
                <div className="w-full bg-white">
                  <Link
                    to="/instructions"
                    className="block text-black hover:bg-green-100 px-6 py-2"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t("Instruccions")}
                  </Link>
                  {authenticated && (
                    <Link
                      to="/applicatiu"
                      className="block text-black hover:bg-green-100 px-6 py-2"
                      onClick={() => setMenuOpen(false)}
                    >
                      {t("Aplicatiu")}
                    </Link>
                  )}
                </div>
              )}
            </li>
{/* Dashboard */}
<li className="relative flex items-center w-full">
  <Link
    to="/dashboard-admin"
    className={`flex items-center gap-1 w-full px-4 py-2 ${
      active === "Dashboard" ? "text-blue-600" : "text-black"
    }`}
    onClick={() => {
      setActive("Dashboard");
      setMenuOpen(false);
    }}
  >
    <FiBarChart2 className="inline-block mr-1" />
    Dashboard
  </Link>
</li>
            {/* Recursos */}
            <li className="relative flex items-center w-full">
              <button
                className="flex items-center text-black w-full px-4 py-2 focus:outline-none"
                onClick={() => setRecursosOpen(!recursosOpen)}
              >
                <span>{t("Recursos")}</span>
                <AiOutlineCaretDown className="ml-auto" />
              </button>
              {recursosOpen && (
                <div className="w-full bg-white">
                  <Link
                    to="/situacio"
                    className="block text-black hover:bg-green-100 px-6 py-2"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t("Situacio daprenentatge")}
                  </Link>
                  <Link
                    to="/dossier"
                    className="block text-black hover:bg-green-100 px-6 py-2"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t("ui.dossier.title", "Materials Didàctics")}
                  </Link>
                  <Link
                    to="/materials"
                    className="block text-black hover:bg-green-100 px-6 py-2"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t("Materials necessaris")}
                  </Link>
                </div>
              )}
            </li>

            {/* Aula */}
            {(userRole === "professor" || userRole === "admin") && (
              <li className="relative flex items-center w-full">
                <Link
                  to="/aula"
                  data-professor-tutorial="classrooms-nav"
                  className={`flex items-center gap-1 w-full px-4 py-2 ${
                    active === "Aula" ? "text-blue-600" : "text-black"
                  }`}
                  onClick={() => {
                    setActive("Aula");
                    setMenuOpen(false);
                  }}
                >
                  <SchoolIcon className="inline-block mr-1" />
                  <span>{t("Aula")}</span>
                </Link>
              </li>
            )}

            {/* Centres */}
            <li className="relative flex items-center w-full">
              <button
                className="flex items-center text-black w-full px-4 py-2 focus:outline-none"
                onClick={() => {
                  setMenuOpen(false);
                }}
              >
                <FaBuilding className="inline-block mr-1" />
                <span>{t("Centres")}</span>
              </button>
            </li>


            {/* (Eliminado) Famílies */}
            {/* Antes había un item deshabilitado de Famílies */}

            {/* Perfil de usuario en móvil */}
            {authenticated ? (
              <li className="relative flex flex-col items-start w-full mt-4">
                <button
                  className="flex items-center w-full px-4 py-2 focus:outline-none"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <img
                    src={accountAvatarUrl}
                    alt={t("avatar.alt", "User avatar")}
                    className="w-8 h-8 rounded-full object-contain bg-green-50"
                    onError={() => setAvatarUrl("/images/default-avatar.png")}
                  />
                  <div className="ml-2 text-left">
                    <div className="text-xs text-black">
                      {(keycloak.tokenParsed?.name?.split(" ")[0]) ||
                        keycloak.tokenParsed?.preferred_username}
                    </div>
                    <div className="text-xs text-gray-500">{userRole}</div>
                  </div>
                  <AiOutlineCaretDown className="ml-auto" />
                </button>
                {userMenuOpen && (
                  <div className="w-full bg-white shadow-lg rounded-md p-2 z-10 border border-green-200">
                    <Link
                      to="/account"
                      className="block text-black hover:bg-green-100 px-6 py-2"
                      onClick={() => setMenuOpen(false)}
                    >
                      {t("Compte")}
                    </Link>
                    <Link
                      to="/reset-password"
                      className="block text-black hover:bg-green-100 px-6 py-2"
                      onClick={() => setMenuOpen(false)}
                    >
                      {t("Restableix la contrasenya")}
                    </Link>
                    {userRole === "professor" ? (
                      <button
                        type="button"
                        className="block text-emerald-700 hover:bg-green-100 px-6 py-2 w-full text-left"
                        onClick={() => {
                          dispatchProfessorTutorialRestart();
                          setMenuOpen(false);
                          setUserMenuOpen(false);
                        }}
                      >
                        {t("professorOnboardingTutorial.actions.restart", "Tornar a obrir el tutorial")}
                      </button>
                    ) : null}
                    <button
                      onClick={() => {
                        handleLogout();
                        setMenuOpen(false);
                      }}
                      className="w-full text-left text-black hover:bg-green-100 px-6 py-2"
                    >
                      {t("Sortir")}
                    </button>
                  </div>
                )}
              </li>
            ) : (
               <>
    {/* 👉 Registro profesorado */}
    <li className="mt-4 w-full">
      <Link
        to="/registre-professorat"
        className="block w-full text-center px-4 py-2
                   border border-green-500 text-green-600
                   rounded-lg hover:bg-green-50 transition"
        onClick={() => setMenuOpen(false)}
      >
        Ets docent? Registra’t
      </Link>
    </li>

    {/* Login */}
    <li className="mt-2 w-full">
      <button
        className="px-2 py-2 w-full bg-green-400 text-white rounded-lg hover:bg-green-500 transition text-center"
        onClick={() => {
          handleLogin();
          setMenuOpen(false);
        }}
      >
        {t("Iniciar Sessió")}
      </button>
    </li>
  </>
)}{/* ⬇️ pega aquí el nuevo bloque */}
            {/* Contacte tècnic (móvil) */}
            <li className="w-full">
              <Link
                to="/technical-support"
                className="flex items-center gap-1 w-full px-4 py-2 text-black hover:bg-green-100"
                onClick={() => {
                  setActive("Suport tècnic");
                  setMenuOpen(false);
                }}
              >
                <FiTool className="inline-block mr-1" />
                {t("Suport tècnic")}
              </Link>
            </li>
            {/* ⬆️ hasta aquí */}
            </ul>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
